import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { User } from '@sss/sdk';
import { sdk } from '../../lib/sdk';

interface Props {
  me: User;
  onUpdated: (user: User) => void;
}

const FIELDS: Array<{ key: 'displayName' | 'firstName' | 'middleName' | 'lastName' | 'userName' | 'company'; type?: string }> = [
  { key: 'displayName' },
  { key: 'firstName' },
  { key: 'middleName' },
  { key: 'lastName' },
  { key: 'userName' },
  { key: 'company' },
];

export default function ProfileTab({ me, onUpdated }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => pickEditable(me));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  function update(key: string, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    setSuccess(false);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      // Only send fields that actually changed and are non-empty (the API
      // requires `.min(1)` on each).
      const diff: Partial<User> = {};
      for (const { key } of FIELDS) {
        const original = (me[key] ?? '') as string;
        const next = form[key] || '';
        if (next !== original && next.length > 0) {
          (diff as Record<string, string>)[key] = next;
        }
      }
      if (Object.keys(diff).length === 0) {
        setSuccess(true);
        return;
      }
      const updated = await sdk.users.update(diff);
      onUpdated(updated);
      setForm(pickEditable(updated));
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={onSubmit} style={form_}>
      <h2 style={{ marginTop: 0 }}>{t('settings.profile.title')}</h2>
      <p style={{ color: '#666' }}>{t('settings.profile.description')}</p>

      <div style={readonly}>
        <span style={readonlyLabel}>{t('auth.email')}</span>
        <span>{me.email}</span>
        <EmailVerificationStatus verified={me.isEmailVerified} />
      </div>

      {FIELDS.map(({ key, type }) => (
        <label key={key} style={label}>
          {t(`settings.profile.${key}`)}
          <input
            type={type || 'text'}
            value={form[key] || ''}
            onChange={(e) => update(key, e.target.value)}
            style={input}
          />
        </label>
      ))}

      <div style={actions}>
        <button type="submit" disabled={saving} style={btn}>
          {saving ? t('common.loading') : t('common.save')}
        </button>
        {success && <span style={{ color: 'green' }}>{t('settings.profile.saveSuccess')}</span>}
        {error && <span style={{ color: 'red' }}>{error}</span>}
      </div>
    </form>
  );
}

function EmailVerificationStatus({ verified }: { verified: boolean }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sent' | 'failed'>('idle');

  if (verified) {
    return <span style={statusVerified}>✓ {t('settings.profile.emailStatus.verified')}</span>;
  }

  async function onResend() {
    setBusy(true);
    setStatus('idle');
    try {
      await sdk.auth.sendVerificationEmail();
      setStatus('sent');
    } catch {
      setStatus('failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={statusRow}>
      <span style={statusUnverified}>⚠ {t('settings.profile.emailStatus.notVerified')}</span>
      {status === 'sent' ? (
        <span style={{ color: '#066' }}>{t('verifyEmail.sent')}</span>
      ) : (
        <button type="button" onClick={onResend} disabled={busy} style={resendBtn}>
          {busy ? '…' : t('verifyEmail.resend')}
        </button>
      )}
      {status === 'failed' && <span style={{ color: 'red' }}>{t('verifyEmail.failed')}</span>}
    </div>
  );
}

function pickEditable(u: User) {
  return {
    displayName: u.displayName || '',
    firstName: u.firstName || '',
    middleName: u.middleName || '',
    lastName: u.lastName || '',
    userName: u.userName || '',
    company: u.company || '',
  };
}

const form_: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '1rem' };
const label: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.25rem' };
const input: React.CSSProperties = { padding: '0.5rem', border: '1px solid #ccc', borderRadius: 4, font: 'inherit' };
const actions: React.CSSProperties = { display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.5rem' };
const btn: React.CSSProperties = { padding: '0.5rem 1rem', cursor: 'pointer' };
const readonly: React.CSSProperties = { margin: 0, color: '#666', display: 'flex', flexDirection: 'column', gap: '0.25rem' };
const readonlyLabel: React.CSSProperties = { fontSize: '0.85rem', color: '#888' };
const statusRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' };
const statusVerified: React.CSSProperties = { color: '#066', fontSize: '0.85rem', marginTop: '0.25rem' };
const statusUnverified: React.CSSProperties = { color: '#a06000', fontSize: '0.85rem' };
const resendBtn: React.CSSProperties = { padding: '0.25rem 0.75rem', fontSize: '0.85rem', cursor: 'pointer' };
